import type { EventoSse } from "@copi/shared";

import { SseConnection } from "./sse-connection";

/**
 * Singleton global de la conexión SSE (ADR 018).
 *
 * **Por qué singleton, no estado del componente.**
 *
 * El hook `useSse` se monta en `AppShell`. En desarrollo, React StrictMode
 * dispara un ciclo `mount → cleanup → mount` extra; cualquier re-render
 * sutil del shell o cambio de referencia en las deps del `useEffect`
 * (queryClient, callbacks no memoizados, etc.) reproduce el mismo ciclo.
 * Si cada ciclo creara una nueva `SseConnection`, el server vería un loop
 * de conectar/desconectar cada pocos cientos de milisegundos — exactamente
 * el bug observado en producción.
 *
 * Movemos la conexión **fuera de React**: vive en este módulo como
 * singleton, indiferente a la cantidad de veces que el componente monte
 * o se re-renderee. El `useEffect` solo:
 *  - Asegura que la conexión esté arriba cuando `enabled = true`
 *    (idempotente — si ya está, no hace nada).
 *  - Registra un listener para invalidar TanStack Query con cada evento.
 *  - Cierra la conexión al detectar `enabled = false` (logout).
 *
 * El cleanup del effect (que React invoca también al re-renderear con deps
 * "nuevas") solo desuscribe el listener — **NO cierra la conexión**. Así
 * los ciclos espurios de StrictMode/re-render quedan absorbidos sin
 * impacto observable en el server.
 *
 * Multi-instancia/futuro: ver §3.6 del ADR 018. La interfaz pública del
 * manager no cambia si en algún momento la conexión la hace un Service
 * Worker o Redis pub/sub fan-out — solo cambia la implementación interna.
 */

type Listener = (evento: EventoSse) => void;

interface ConectarOpciones {
  baseUrl: string;
  onRefreshNeeded?: () => Promise<string | null>;
}

class SseManager {
  private conexion: SseConnection | null = null;
  private readonly listeners = new Set<Listener>();

  /**
   * Asegura que la conexión esté establecida. Idempotente: llamar varias
   * veces con la misma URL no abre conexiones nuevas. Si los parámetros
   * cambiaron (e.g. distinto baseUrl), se desconecta la anterior y se
   * abre una nueva.
   */
  conectar(opciones: ConectarOpciones): void {
    if (this.conexion) {
      // Ya hay una conexión viva — el caller solo está confirmando que la
      // sesión sigue activa. No-op.
      return;
    }

    this.conexion = new SseConnection({
      baseUrl: opciones.baseUrl,
      onRefreshNeeded: opciones.onRefreshNeeded,
      onEvento: (evento) => {
        this.fanOut(evento);
      },
    });
    this.conexion.connect();
  }

  /**
   * Cierra la conexión. Idempotente. Llamar en logout o cuando la sesión
   * deje de ser válida (no en cada re-render del componente).
   */
  desconectar(): void {
    if (!this.conexion) return;
    this.conexion.disconnect();
    this.conexion = null;
  }

  /**
   * Registra un listener para los eventos. Devuelve una función `unsubscribe`
   * que el caller debe invocar al desmontar. Múltiples suscriptores son
   * independientes — cada uno recibe todos los eventos.
   *
   * Importante: desuscribirse NO cierra la conexión. La conexión vive
   * mientras `enabled = true` independientemente de cuántos listeners haya.
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Para tests y observabilidad. */
  estaConectado(): boolean {
    return this.conexion !== null;
  }

  /** Para tests: limpia el estado completamente. */
  resetParaTests(): void {
    this.desconectar();
    this.listeners.clear();
  }

  private fanOut(evento: EventoSse): void {
    for (const listener of this.listeners) {
      try {
        listener(evento);
      } catch (err) {
        // Un listener buggy no debe matar a los demás.
        console.error("[SSE] listener lanzó:", err);
      }
    }
  }
}

/** Instancia única del manager — vive a nivel módulo, fuera de React. */
export const sseManager = new SseManager();
